import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Cookie, Shield, BarChart3, Megaphone, Settings, Check, X } from "lucide-react";

const COOKIE_CONSENT_KEY = "groemitz_cookie_consent";

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [consent, setConsent] = useState({
    necessary: true, // Always true, cannot be changed
    functional: false,
    statistical: false,
    marketing: false,
  });

  useEffect(() => {
    // Hide banner on payment pages, admin and staff routes – PayPal SDK is essential
    // for payment and must load without cookie-consent friction (DSGVO-konform:
    // Zahlungsanbieter ist berechtigtes Interesse / Vertragserfüllung).
    const path = window.location.pathname || "";
    if (path.startsWith("/pay/") || path.startsWith("/admin") || path.startsWith("/staff") || path === "/checkin") {
      setShowBanner(false);
      return;
    }
    // Check if user has already made a choice
    const savedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (savedConsent) {
      setConsent(JSON.parse(savedConsent));
      setShowBanner(false);
    } else {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = (newConsent) => {
    const consentData = {
      ...newConsent,
      necessary: true, // Always true
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentData));
    setConsent(consentData);
    setShowBanner(false);
    setShowSettings(false);
  };

  const acceptAll = () => {
    saveConsent({
      necessary: true,
      functional: true,
      statistical: true,
      marketing: true,
    });
  };

  const rejectAll = () => {
    saveConsent({
      necessary: true,
      functional: false,
      statistical: false,
      marketing: false,
    });
  };

  const saveSettings = () => {
    saveConsent(consent);
  };

  const openSettings = () => {
    setShowBanner(false);
    setShowSettings(true);
  };

  // Export function to open settings from footer
  useEffect(() => {
    window.openCookieSettings = () => setShowSettings(true);
    return () => {
      delete window.openCookieSettings;
    };
  }, []);

  return (
    <>
      {/* Cookie Banner */}
      {showBanner && (
        <div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6 bg-slate-900/95 backdrop-blur-lg border-t border-slate-700 shadow-2xl animate-in slide-in-from-bottom duration-500">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-8">
              {/* Text */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Cookie className="w-5 h-5 text-sky-400" />
                  <h3 className="text-lg font-semibold text-white">Cookie-Einstellungen</h3>
                </div>
                <p className="text-slate-300 text-sm leading-relaxed">
                  Wir verwenden Cookies und vergleichbare Technologien, um unsere Website sicher und 
                  benutzerfreundlich zu gestalten sowie Inhalte und Anzeigen zu personalisieren.
                </p>
              </div>
              
              {/* Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 lg:flex-shrink-0">
                <Button
                  variant="outline"
                  onClick={openSettings}
                  className="border-slate-600 text-slate-300 hover:bg-slate-800"
                  data-testid="cookie-settings-btn"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Einstellungen
                </Button>
                <Button
                  variant="outline"
                  onClick={rejectAll}
                  className="border-slate-600 text-slate-300 hover:bg-slate-800"
                  data-testid="cookie-reject-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  Nur Notwendige
                </Button>
                <Button
                  onClick={acceptAll}
                  className="bg-sky-500 hover:bg-sky-600 text-white"
                  data-testid="cookie-accept-btn"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Alle akzeptieren
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Cookie className="w-6 h-6 text-sky-500" />
              Cookie-Einstellungen
            </DialogTitle>
          </DialogHeader>

          <div className="py-4 space-y-6">
            <p className="text-slate-600 text-sm">
              Wir verwenden Cookies und vergleichbare Technologien, um unsere Website sicher und 
              benutzerfreundlich zu gestalten sowie Inhalte und Anzeigen zu personalisieren.
              Beim ersten Besuch unserer Website erscheint ein Consent-Banner. Dort können Sie 
              auswählen, ob Sie der Verwendung folgender Cookie-Kategorien zustimmen oder diese 
              ablehnen möchten.
            </p>

            {/* Necessary Cookies */}
            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-5 h-5 text-green-600" />
                    <h4 className="font-semibold text-slate-900">Notwendige Cookies</h4>
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                      Immer aktiv
                    </span>
                  </div>
                  <p className="text-sm text-slate-600">
                    Notwendige Cookies sind technisch erforderlich, damit die Website ordnungsgemäß 
                    funktioniert (z. B. zur Speicherung Ihrer Datenschutzeinstellungen oder zur 
                    Gewährleistung der Sicherheit). Diese Cookies können nicht deaktiviert werden.
                  </p>
                  <p className="text-xs text-slate-500 mt-2">
                    Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an der 
                    technisch fehlerfreien Bereitstellung der Website).
                  </p>
                </div>
                <Switch checked={true} disabled className="data-[state=checked]:bg-green-500" />
              </div>
            </div>

            {/* Functional Cookies */}
            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Settings className="w-5 h-5 text-blue-600" />
                    <h4 className="font-semibold text-slate-900">Funktionale Cookies</h4>
                  </div>
                  <p className="text-sm text-slate-600">
                    Dienen der Bereitstellung erweiterter Funktionen und der Personalisierung der Website.
                  </p>
                  <p className="text-xs text-slate-500 mt-2">
                    Rechtsgrundlage: Art. 6 Abs. 1 lit. a DSGVO (Einwilligung).
                  </p>
                </div>
                <Switch
                  checked={consent.functional}
                  onCheckedChange={(checked) => setConsent({ ...consent, functional: checked })}
                  data-testid="cookie-functional-switch"
                />
              </div>
            </div>

            {/* Statistical Cookies */}
            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <BarChart3 className="w-5 h-5 text-purple-600" />
                    <h4 className="font-semibold text-slate-900">Statistische Cookies</h4>
                  </div>
                  <p className="text-sm text-slate-600">
                    Helfen uns, das Nutzungsverhalten anonymisiert zu analysieren, um unsere Website zu optimieren.
                  </p>
                  <p className="text-xs text-slate-500 mt-2">
                    Rechtsgrundlage: Art. 6 Abs. 1 lit. a DSGVO (Einwilligung).
                  </p>
                </div>
                <Switch
                  checked={consent.statistical}
                  onCheckedChange={(checked) => setConsent({ ...consent, statistical: checked })}
                  data-testid="cookie-statistical-switch"
                />
              </div>
            </div>

            {/* Marketing Cookies */}
            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Megaphone className="w-5 h-5 text-orange-600" />
                    <h4 className="font-semibold text-slate-900">Marketing-Cookies</h4>
                  </div>
                  <p className="text-sm text-slate-600">
                    Ermöglichen es uns und Drittanbietern, relevante Werbung anzuzeigen und die Wirksamkeit 
                    von Kampagnen zu messen.
                  </p>
                  <p className="text-xs text-slate-500 mt-2">
                    Rechtsgrundlage: Art. 6 Abs. 1 lit. a DSGVO (Einwilligung).
                  </p>
                </div>
                <Switch
                  checked={consent.marketing}
                  onCheckedChange={(checked) => setConsent({ ...consent, marketing: checked })}
                  data-testid="cookie-marketing-switch"
                />
              </div>
            </div>

            {/* Widerruf Info */}
            <div className="p-4 rounded-xl bg-sky-50 border border-sky-200">
              <h4 className="font-semibold text-sky-900 mb-2">Widerruf & Anpassung</h4>
              <p className="text-sm text-sky-800">
                Sie können Ihre Einwilligung jederzeit mit Wirkung für die Zukunft widerrufen oder anpassen. 
                Nutzen Sie hierzu den Button „Cookie-Einstellungen" im Footer unserer Website.
              </p>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-3">
            <Button
              variant="outline"
              onClick={rejectAll}
              className="w-full sm:w-auto"
            >
              Nur Notwendige
            </Button>
            <Button
              variant="outline"
              onClick={acceptAll}
              className="w-full sm:w-auto"
            >
              Alle akzeptieren
            </Button>
            <Button
              onClick={saveSettings}
              className="w-full sm:w-auto bg-sky-500 hover:bg-sky-600"
              data-testid="cookie-save-btn"
            >
              Auswahl speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieConsent;
